import { useQuery } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { mediaBase } from './configs';
import type {
  VodDurationUsage,
  VodVolumeUsage,
  VodUsageDownloadType,
} from './vod-usage-types';

// ============================================
// Query keys
// ============================================

export const vodUsageKeys = {
  duration: (serviceId: string, year: number, type: string) =>
    ['vod', 'usage', 'duration', serviceId, { year, type }] as const,
  volume: (serviceId: string, year: number, type: string) =>
    ['vod', 'usage', 'volume', serviceId, { year, type }] as const,
};

// ============================================
// Queries
// ============================================

export function useVodDurationUsage(
  serviceId: string,
  year: number,
  type = '',
) {
  return useQuery<VodDurationUsage[]>({
    queryKey: vodUsageKeys.duration(serviceId, year, type),
    enabled: !!serviceId && !!year,
    queryFn: async () => {
      const params: Record<string, string> = { year: String(year) };
      if (type) params.type = type;
      const res = await serviceApiV3.get(
        `${mediaBase(serviceId, 'vod')}/usage/transcode/duration`,
        { params },
      );
      return res.data?.data ?? res.data;
    },
  });
}

export function useVodVolumeUsage(
  serviceId: string,
  year: number,
  type = '',
) {
  return useQuery<VodVolumeUsage[]>({
    queryKey: vodUsageKeys.volume(serviceId, year, type),
    enabled: !!serviceId && !!year,
    queryFn: async () => {
      const params: Record<string, string> = { year: String(year) };
      if (type) params.type = type;
      const res = await serviceApiV3.get(
        `${mediaBase(serviceId, 'vod')}/usage/transcode/volume`,
        { params },
      );
      return res.data?.data ?? res.data;
    },
  });
}

/**
 * Download usage data as CSV blob. Returns a `Blob` — the caller creates an
 * object URL and triggers a browser download.
 */
export async function fetchVodUsageCsv(
  serviceId: string,
  usageType: VodUsageDownloadType,
  year: number,
  type = '',
): Promise<Blob> {
  const params: Record<string, string> = { year: String(year) };
  if (type) params.type = type;
  const res = await serviceApiV3.get(
    `${mediaBase(serviceId, 'vod')}/usage/transcode/${usageType}/download`,
    { params, responseType: 'blob' },
  );
  return res.data;
}
